import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useSnackBar } from '../../../../contexts/snackbar';
import { IPaymentTask } from '../../../../interfaces/delivery-company/tasks';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import GenericTable from '../../../common/generic-table/generic-table';
import { Workflow } from '../../../../enums/orders';
import { displayLocation } from '../../../../utils/locations';
import CancelIcon from '@mui/icons-material/Cancel';
import { PaymentTaskState } from '../../../../enums/tasks';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { displayMoney } from '../../../../utils/money';
import AddIcon from '@mui/icons-material/Add';
import CreatePaymentTask from '../../../common/create-payment-task/create-payment-task';
import {
  IMerchantOrdersInvoice,
  InvoicePaymentMethod,
  MerchantOrdersInvoiceState,
} from '../../../../interfaces/common/merchant-invoices';
import InvoicePDFDialog from '../../../common/invoice-pdf-dialog/invoice-pdf-dialog';
import InvoiceWaybillsDialog from './invoice-waybill-dialog';
import InvoiceWaybillDownload from './invoice-waybill-dialog';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import ReplayIcon from '@mui/icons-material/Replay';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { user_is_admin } from '../../../../utils/permissions';
import { format } from 'date-fns';
import PaymentMethodSelector from '../../../common/payment-method-selector/payment-method-selector';

interface EditingInvoiceProps {
  invoice: IMerchantOrdersInvoice;
  updateInvoice: (invoice: IMerchantOrdersInvoice) => void;
}

function EditingInvoice({ invoice, updateInvoice }: EditingInvoiceProps) {
  const [paymentTaskList, setPaymentTaskList] = useState<IPaymentTask[]>([]);
  const { user, config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [cancellingTask, setCancellingTask] = useState<IPaymentTask | null>(
    null
  );
  const [schedulingNewPayment, setSchedulingNewPayment] = useState(false);
  const [completingTask, setCompletingTask] = useState<IPaymentTask | null>(
    null
  );
  const [confirmingReturn, setConfirmingReturn] = useState<IPaymentTask | null>(
    null
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<InvoicePaymentMethod>(
    invoice.payment_method
  );
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  // Check if there's an active payment task
  const hasActivePaymentTask = paymentTaskList.some(task => 
    [PaymentTaskState.SCHEDULED, PaymentTaskState.OUT_FOR_PAYMENT, 
     PaymentTaskState.IN_PROGRESS, PaymentTaskState.AWAITING_RETURN].includes(task.status)
  );

  // Check if invoice is completed
  const isInvoiceCompleted = [MerchantOrdersInvoiceState.PAID, 
    MerchantOrdersInvoiceState.CANCELLED].includes(invoice.status);

  // Can change payment method only if no active tasks and invoice not completed
  const canChangePaymentMethod = !hasActivePaymentTask && !isInvoiceCompleted;

  const cancelTask = async (queryTask: IPaymentTask) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${queryTask.id}/`;
    setResponse(undefined);
    setMessage('');
    try {
      const response = await axios.delete(endpoint, config);
      setPaymentTaskList(
        paymentTaskList.map((task) =>
          task.id !== queryTask.id
            ? task
            : {
                ...task,
                status: PaymentTaskState.CANCELLED,
              }
        )
      );
      setMessage('Payment task cancelled.');
      updateInvoice({ ...invoice, status: MerchantOrdersInvoiceState.FAILED });
      setResponse(response);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to cancel payment task: ' + e.response?.statusText);
      } else {
        setMessage('Failed to cancel payment task');
      }
    }
    setIsLoading(false);
  };

  const completeTask = async (queryTask: IPaymentTask, reason: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${queryTask.id}/`;
    try {
      const response = await axios.patch(
        endpoint,
        {
          completion_method: reason,
          status: PaymentTaskState.COMPLETED,
        },
        config
      );
      console.log(response);
      setPaymentTaskList(
        paymentTaskList.map((task) =>
          task.id !== queryTask.id
            ? task
            : {
                ...task,
                status: PaymentTaskState.COMPLETED,
              }
        )
      );
      setMessage('Payment task completed successfully.');
      updateInvoice({ ...invoice, status: MerchantOrdersInvoiceState.PAID });
      setResponse(response);
    } catch (e) {
      console.log(e);
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to complete payment task: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to complete payment task');
      }
    }
    setIsLoading(false);
  };

  const confirmReturn = async (queryTask: IPaymentTask) => {
    if (user) {
      const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${queryTask.id}/`;
      try {
        const response = await axios.patch(
          endpoint,
          {
            notes: [`Return confirmed by ${user.name} ${user?.phone_number}`],
            status: PaymentTaskState.FAILED,
          },
          config
        );
        console.log(response);
        setPaymentTaskList(
          paymentTaskList.map((task) =>
            task.id !== queryTask.id
              ? task
              : {
                  ...task,
                  status: PaymentTaskState.FAILED,
                }
          )
        );
        setMessage('Payment task marked as failed.');
        updateInvoice({
          ...invoice,
          status: MerchantOrdersInvoiceState.FAILED,
        });
        setResponse(response);
      } catch (e) {
        console.log(e);
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage(
            'Failed to mark task as failed: ' + e.response?.statusText
          );
        } else {
          setMessage('Failed to mark task as failed');
        }
      }
      setIsLoading(false);
    } else {
      alert('Please logout and login back and try again');
    }
  };

  const updatePaymentMethod = async (method: InvoicePaymentMethod) => {
    setIsUpdatingPaymentMethod(true);
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/merchant-invoice/${invoice.id}/`;
      await axios.patch(endpoint, { payment_method: method }, config);
      setSelectedPaymentMethod(method);
      updateInvoice({ ...invoice, payment_method: method });
      showMessage('Payment method updated successfully.', 'success');
    } catch (e) {
      if (axios.isAxiosError(e)) {
        showMessage('Failed to update payment method: ' + e.response?.statusText, 'error');
      } else {
        showMessage('Failed to update payment method', 'error');
      }
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  useEffect(() => {
    const fetchAndSetTasks = async () => {
      setIsLoading(true);
      setMessage('');
      setResponse(undefined);
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/?invoice=${invoice.id}&page_size=200`;
        const result = await axios.get<{
          count: number;
          results: IPaymentTask[];
        }>(endpoint, config);
        setPaymentTaskList(result.data.results);
        setResponse(result);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setMessage(
            'Failed to load payment tasks of this invoice: ' +
              e.response?.statusText
          );
          setResponse(e.response);
        } else {
          setMessage('Failed to load payment tasks of this invoice');
        }
      }
      setIsLoading(false);
    };

    fetchAndSetTasks();
  }, [invoice.id]);
  return (
    <div>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        Invoice Details #{invoice.id}
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight="bold">Merchant</Typography>
            <Typography variant="body1">
              {invoice.merchant?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.merchant?.phone_number}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight="bold">Total Due</Typography>
            <Typography variant="body1">
              {displayMoney(invoice.net_due_to_merchant)}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            {isUpdatingPaymentMethod ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Updating...
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Payment Method
                </Typography>
                <PaymentMethodSelector
                  value={selectedPaymentMethod}
                  onChange={(method) => {
                    if (method !== selectedPaymentMethod && canChangePaymentMethod) {
                      updatePaymentMethod(method);
                    }
                  }}
                  disabled={!canChangePaymentMethod}
                  showLabel={false}
                  centered={true}
                  helperText={
                    !canChangePaymentMethod
                      ? (isInvoiceCompleted ? 'Invoice is completed' : 'Active payment task exists')
                      : undefined
                  }
                />
              </>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight="bold">Print</Typography>
            <InvoiceWaybillDownload invoice={invoice} paymentLocation={paymentTaskList.length > 0 ? paymentTaskList[0].merchant_location : null}/>
          </Box>
        </Grid>
      </Grid>

      <br />

      <Typography variant="h4" component="h4" sx={{ textAlign: 'center' }}>
        Payment Attempts
        <br />
        {(invoice.status === MerchantOrdersInvoiceState.FAILED ||
          invoice.status === MerchantOrdersInvoiceState.PENDING_PAYMENT) && (
          <IconButton
            onClick={() => setSchedulingNewPayment(true)}
            disabled={
              invoice.status != MerchantOrdersInvoiceState.PENDING_PAYMENT &&
              invoice.status != MerchantOrdersInvoiceState.FAILED
            }
          >
            <AddIcon fontSize="large" color="primary" />
          </IconButton>
        )}
      </Typography>
      {message != '' && (
        <>
          <Alert
            severity={
              response && response.status >= 200 && response.status < 300
                ? 'success'
                : 'error'
            }
          >
            <>
              {message}

              {response && response.status >= 300 && (
                <ErrorsFlattenner rawErrors={response.data} />
              )}
            </>
          </Alert>
          <br />
        </>
      )}
      <GenericTable<IPaymentTask>
        sourceView="editing-invoice"
        items={paymentTaskList}
        getItemUID={(task) => task.id}
        columns={[
          {
            header: <b>Task ID</b>,
            id: 'task_id',
            display_body: (task) => <>{task.id}</>,
          },
          {
            header: <b>Created At</b>,
            id: 'created_at',
            display_body: (task) => (
              <>{new Date(task.creation_date ?? '').toLocaleString()}</>
            ),
          },
          {
            header: <b>Driver</b>,
            id: 'driver',
            display_body: (task) => (
              <>
                {task.driver?.name} {task.driver?.phone_number}
              </>
            ),
          },
          {
            header: <b>Status</b>,
            id: 'status',
            display_body: (task) => (
              <>
                {task.status}
                {task.status === PaymentTaskState.COMPLETED && (
                  <>
                    <br />
                    {task.completion_method}
                    {task.completed_at && <><br />{format(task.completed_at, 'dd-MM-yyyy HH:mm')}</>}
                  </>
                )}
              </>
            ),
          },
          {
            header: <b>Payment Location</b>,
            id: 'location',
            display_body: (task) => (
              <>{displayLocation(task.merchant_location)}</>
            ),
          },
          {
            header: <b>Receive Return</b>,
            id: 'complete return',
            display_body: (task) => (
              <IconButton
                disabled={task.status != PaymentTaskState.AWAITING_RETURN}
                onClick={() => setConfirmingReturn(task)}
              >
                <ReplayIcon fontSize="large" />
              </IconButton>
            ),
          },
          {
            header: (
              <b>
                Complete Task <br /> (Admin only)
              </b>
            ),
            id: 'complete payment',
            display_body: (task) => (
              <IconButton
                disabled={
                  [
                    PaymentTaskState.COMPLETED,
                    PaymentTaskState.CANCELLED,
                    PaymentTaskState.FAILED,
                  ].includes(task.status) || !user_is_admin(user)
                }
                onClick={() => setCompletingTask(task)}
              >
                <CreditScoreIcon fontSize="large" />
              </IconButton>
            ),
          },
          {
            id: 'cancel_request',
            header: <p>Cancel</p>,
            display_body: (task) => {
              return (
                <IconButton
                  disabled={[
                    PaymentTaskState.COMPLETED,
                    PaymentTaskState.CANCELLED,
                    PaymentTaskState.FAILED,
                  ].includes(task.status)}
                  onClick={() => setCancellingTask(task)}
                >
                  <CancelIcon fontSize="large" />
                </IconButton>
              );
            },
          },
        ]}
        selectedItems={new Map()}
        isLoading={isLoading}
        fullWidth
      />

      {cancellingTask != null && (
        <ConfirmAction
          message={'Are you sure you want to cancel this payment task?'}
          action={() => cancelTask(cancellingTask)}
          isOpen={cancellingTask != null}
          handleClose={() => setCancellingTask(null)}
          isSevereAction={true}
          confirmSevereActionTarget="cancel payment"
        >
          <Alert severity="warning">This operation cannot be undone</Alert>
        </ConfirmAction>
      )}

      {completingTask != null && (
        <ConfirmAction
          message={'Are you sure you want to mark this task as completed?'}
          action={(reason) => completeTask(completingTask, reason ?? '')}
          isOpen={completingTask != null}
          requireReason
          handleClose={() => setCompletingTask(null)}
          isSevereAction={true}
          confirmSevereActionTarget="complete payment"
        >
          <Alert severity="warning">This operation cannot be undone</Alert>
        </ConfirmAction>
      )}

      {confirmingReturn != null && (
        <ConfirmAction
          message={
            'Are you sure you want to confirm the return of this invoice?'
          }
          action={() => confirmReturn(confirmingReturn)}
          isOpen={confirmingReturn != null}
          handleClose={() => setConfirmingReturn(null)}
          isSevereAction={true}
          confirmSevereActionTarget="confirm return"
        >
          <Alert severity="warning">This operation cannot be undone</Alert>
        </ConfirmAction>
      )}

      <CustomDialog
        open={schedulingNewPayment}
        fullWidth
        onClose={() => setSchedulingNewPayment(false)}
      >
        <DialogContent>
          {invoice.merchant && (
            <CreatePaymentTask
              invoice={invoice}
              onCreated={(p) => {
                setPaymentTaskList([p, ...paymentTaskList]);
                updateInvoice({
                  ...invoice,
                  status: MerchantOrdersInvoiceState.PAYMENT_SCHEDULED,
                });
              }}
            />
          )}
        </DialogContent>
      </CustomDialog>
    </div>
  );
}

export default EditingInvoice;
